
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut, User, Bell, History, FileQuestion, Siren, X, Menu,
  PhoneCall, CreditCard, MessageSquare, ShieldCheck, LayoutGrid,
  Clock, Home,
} from "lucide-react";
import api, { IMAGE_BASE_URL } from "../api/Api";
import { toast } from "sonner";
import Swal from "sweetalert2";
import { socket } from "../api/socket";

const appStyles = `
  .sos-pulse-always {
    background-color: #dc3545 !important;
    animation: radar-pulse 1.5s infinite;
  }
  @keyframes radar-pulse {
    0%   { box-shadow: 0 0 0 0   rgba(220,53,69,0.7); }
    70%  { box-shadow: 0 0 0 10px rgba(220,53,69,0);   }
    100% { box-shadow: 0 0 0 0   rgba(220,53,69,0);   }
  }

  /* ── Backdrop ── */
  .hdr-backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 1040;
    opacity: 0; visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }
  .hdr-backdrop.show { opacity: 1; visibility: visible; }

  /* ── Generic drawer (left by default) ── */
  .hdr-drawer {
    position: fixed; top: 0; left: 0;
    width: 300px; height: 100%;
    background: #fff;
    z-index: 1050;
    display: flex; flex-direction: column;
    transform: translateX(-100%);
    transition: transform 0.32s cubic-bezier(0.25,1,0.5,1);
    will-change: transform;
  }
  .hdr-drawer.open { transform: translateX(0); }

  /* ── Notification drawer (right side) ── */
  .hdr-drawer.notif-drawer {
    left: auto; right: 0;
    box-shadow: -6px 0 32px rgba(0,0,0,0.12);
    transform: translateX(100%);
  }
  .hdr-drawer.notif-drawer.open { transform: translateX(0); }

  /* ── Nav sidebar shadow ── */
  .hdr-drawer.nav-drawer {
    box-shadow: 6px 0 32px rgba(0,0,0,0.12);
  }

  /* ── Sidebar nav links ── */
  .sidebar-link {
    display: flex; align-items: center;
    padding: 13px 18px; margin: 3px 10px;
    color: #495057; font-weight: 500; font-size: 14px;
    border-radius: 10px; cursor: pointer;
    transition: background 0.18s, color 0.18s;
    text-decoration: none;
  }
  .sidebar-link:hover  { background: #f0f4ff; color: #0d6efd; }
  .sidebar-link.active { background: #e7f1ff; color: #0d6efd; font-weight: 600; }

  /* ── Bottom nav ── */
  .bottom-nav-container {
    position: fixed; bottom: 0; left: 0; width: 100%;
    background: #fff;
    z-index: 1030;
    box-shadow: 0 -2px 20px rgba(0,0,0,0.07);
    padding-bottom: env(safe-area-inset-bottom);
    border-top-left-radius: 18px;
    border-top-right-radius: 18px;
  }
  
  .main-content-area {
    padding-bottom: 90px;
    min-height: 100%;
    background-color: #f5f6fa;
    /* CRITICAL FIX FOR PAYU WEBVIEW POPUPS */
    overflow: visible !important; 
    position: relative;
  }

  /* ── Notification items ── */
  .notif-item {
    display: flex; gap: 12px; align-items: flex-start;
    padding: 13px 14px; margin-bottom: 8px;
    border-radius: 12px;
    border: 1px solid #e9ecef;
    cursor: pointer;
    transition: background 0.15s;
    background: #fff;
  }
  .notif-item:hover { background: #f0f4ff; }
  .notif-item.unread { background: #eef4ff; border-color: #c7d9ff; }
  .notif-item.unread:hover { background: #deeaff; }

  /* ── SOS animations ── */
  @keyframes sosPulse {
    0%   { box-shadow: 0 0 0 0   rgba(220,53,69,0.6); }
    70%  { box-shadow: 0 0 0 36px rgba(220,53,69,0);   }
    100% { box-shadow: 0 0 0 0   rgba(220,53,69,0);   }
  }
  @keyframes ringExpand {
    0%   { transform: scale(1);   opacity: 0.55; }
    100% { transform: scale(2.6); opacity: 0;    }
  }
  @keyframes slideUp {
    from { transform: translateY(48px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }
  @keyframes countFlip {
    0%   { transform: translateY(-12px) scale(0.75); opacity: 0; }
    100% { transform: translateY(0)     scale(1);    opacity: 1; }
  }
  @keyframes gpsBlip {
    0%,100% { opacity: 1; } 50% { opacity: 0.25; }
  }

  .sos-ring {
    position: absolute; border-radius: 50%;
    border: 1.5px solid rgba(220,53,69,0.38);
    animation: ringExpand 2.1s ease-out infinite;
    pointer-events: none;
    width: 100%; height: 100%;
  }
  .sos-ring-2 { animation-delay: 0.7s;  }
  .sos-ring-3 { animation-delay: 1.4s;  }
  .sos-card-anim  { animation: slideUp   0.36s cubic-bezier(0.22,1,0.36,1) both; }
  .sos-count-anim { animation: countFlip 0.28s ease both; }

  .sos-send-btn {
    background: #dc3545; color: #fff; border: none;
    border-radius: 14px; padding: 15px 0;
    font-size: 16px; font-weight: 600; width: 100%;
    cursor: pointer; letter-spacing: 0.3px;
    transition: transform 0.1s, background 0.15s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .sos-send-btn:active { transform: scale(0.97); background: #b02a37; }

  .sos-cancel-btn {
    background: rgba(255,255,255,0.07);
    color: rgba(255,255,255,0.6);
    border: 0.5px solid rgba(255,255,255,0.15);
    border-radius: 14px; padding: 14px 0;
    font-size: 15px; font-weight: 500; width: 100%;
    cursor: pointer; transition: background 0.2s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    margin-top: 10px;
  }
  .sos-cancel-btn:hover { background: rgba(255,255,255,0.14); color: #fff; }

  .sos-loc-pill {
    display: inline-flex; align-items: center; gap: 7px;
    background: rgba(40,200,120,0.12); color: #2dbd82;
    border: 0.5px solid rgba(62,207,142,0.3);
    border-radius: 20px; padding: 5px 14px;
    font-size: 12px; font-weight: 500;
  }
  .sos-loc-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #3ecf8e; flex-shrink: 0;
    animation: gpsBlip 1.2s infinite;
  }
`;

const HeaderLayout = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const customerId = localStorage.getItem("customerId");

  const [showSosModal,      setShowSosModal]      = useState(false);
  const [sosCountdown,      setSosCountdown]      = useState(3);
  const [isSidebarOpen,     setIsSidebarOpen]     = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications,     setNotifications]     = useState([]);
  const [profile,           setProfile]           = useState({ fullName: "Guest", customerId: "", cust_img: null });

  const sosTimerRef = useRef(null);

  // ── Inject styles ──────────────────────────────────────────
  useLayoutEffect(() => {
    const el = document.createElement("style");
    el.innerText = appStyles;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  // ── Fetch profile ──────────────────────────────────────────
  useEffect(() => {
    if (!customerId) return;
    api.get("/customer/profile")
      .then(res => setProfile(res.data))
      .catch(() => console.error("Profile sync failed"));
  }, [customerId, location.pathname]);

  // ── Socket + notifications ─────────────────────────────────
  useEffect(() => {
    if (!customerId) return;

    socket.emit("join_booking_room", `global_customer_${customerId}`);

    // Only join numeric room AFTER profile has loaded (avoid the warning on first render)
    const numericId =
      profile?.gustcust_id || profile?.id || profile?.cust_id ||
      profile?.Customer_id || profile?.customer_id;

    if (numericId) {
      socket.emit("join_booking_room", `global_customer_${numericId}`);
    }

    const addNotif = (data, extra = {}) =>
      setNotifications(prev => [
        { id: Date.now(), message: data.message, time: new Date(), isRead: false, ...extra },
        ...prev,
      ]);

    socket.on("trip_created", data => addNotif(data));

    socket.on("trip_reminder", data => {
      addNotif(data, { bookingId: data.bookingId });
      Swal.fire({
        title: "Your Ride is Almost Here! 🚗",
        text: data.message,
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#0d6efd",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "View Trip Details",
        cancelButtonText: "Dismiss",
        customClass: { popup: "rounded-4 shadow-sm" },
      }).then(result => {
        if (result.isConfirmed && data.bookingId) navigate(`/active-trip/${data.bookingId}`);
      });
    });

    return () => { socket.off("trip_created"); socket.off("trip_reminder"); };
  }, [customerId, profile, navigate]);

  // ── SOS countdown ──────────────────────────────────────────
  useEffect(() => {
    if (!showSosModal) return;
    if (sosCountdown === 0) {
      setShowSosModal(false);
      handleTriggerSosApi();
      return;
    }
    const t = setInterval(() => setSosCountdown(p => p - 1), 1000);
    sosTimerRef.current = t;
    return () => clearInterval(t);
  }, [showSosModal, sosCountdown]);

  // ── SOS API ────────────────────────────────────────────────
  const handleTriggerSosApi = () => {
    if (!customerId) { toast.error("User ID missing. Please re-login."); return; }
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }

    navigator.geolocation.getCurrentPosition(
      async ({ coords: { latitude, longitude } }) => {
        const tid = toast.loading("Sending Emergency Alert…");
        try {
          await api.post("/customer/sos/trigger", { customerId, latitude, longitude });
          toast.dismiss(tid);
          toast.success("SOS SIGNAL SENT TO ADMIN!", {
            style: { background: "#dc3545", color: "#fff" },
            icon: <Siren size={20} color="white" />,
            duration: 5000,
          });
        } catch {
          toast.dismiss(tid);
          toast.error("SOS FAILED! Network error.");
        }
      },
      err => { console.error("GPS Error:", err); toast.error("GPS failed. Check your location settings."); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  // ── Logout ─────────────────────────────────────────────────
  const handleLogout = () => {
  setIsSidebarOpen(false);

  Swal.fire({
    html: `
      <div class="d-flex flex-column align-items-center text-center">

        <div 
          class="d-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10 mb-3"
          style="width:85px;height:85px;"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg"
            width="38"
            height="38"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-danger"
          >
            <path d="m16 17 5-5-5-5"></path>
            <path d="M21 12H9"></path>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
          </svg>
        </div>

        <h3 class="fw-bold text-dark mb-2">
          Logout Account?
        </h3>

        <p class="text-muted mb-0" style="font-size:15px;">
          Your current session will be ended securely.
        </p>

      </div>
    `,

    showCancelButton: true,
    confirmButtonText: "Yes, Logout",
    cancelButtonText: "Stay Logged In",

    reverseButtons: true,
    buttonsStyling: false,

    customClass: {
      popup: "rounded-4 border-0 shadow-lg p-3",
      confirmButton:
        "btn btn-danger rounded-3 px-4 py-2 fw-semibold ms-2",
      cancelButton:
        "btn btn-light border rounded-3 px-4 py-2 fw-semibold text-dark",
    },

    backdrop: `rgba(15,23,42,0.6)`,
  }).then((result) => {
    if (result.isConfirmed) {
      ["customerToken", "customerId", "customerMobile"].forEach((k) =>
        localStorage.removeItem(k)
      );

      navigate("/login", { replace: true });
    }
  });
};

  const isActive = path => location.pathname === path;
  const unreadCount = notifications.filter(n => !n.isRead).length;

  const sidebarItems = [
    { label: "Profile",          path: "/profile",          icon: User         },
    { label: "Booking History",  path: "/booking-history",  icon: History      },
    { label: "Payment History",  path: "/payment-history",  icon: CreditCard   },
    { label: "Enquiry",          path: "/enquiry",          icon: FileQuestion },
    { label: "Change MPIN",      path: "/change-mpin",      icon: ShieldCheck  },
    { label: "Contact Office",   path: "/contact-office",   icon: PhoneCall    },
  ];

  const bottomNavItems = [
    { label: "Home",     path: "/dashboard",       icon: Home      },
    { label: "Services", path: "/services",         icon: LayoutGrid},
    { label: "Activity", path: "/activity-history", icon: Clock     },
    { label: "Account",  path: "/profile",          icon: User      },
  ];

  // ── Mark all read when panel opens ────────────────────────
  useEffect(() => {
    if (showNotifications && unreadCount > 0) {
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    }
  }, [showNotifications]);

  return (
    <div className="d-flex flex-column min-vh-100 bg-white">

      {/* ════ SHARED BACKDROP ════ */}
      <div
        className={`hdr-backdrop ${isSidebarOpen || showNotifications ? "show" : ""}`}
        style={{ zIndex: 1040 }}
        onClick={() => { setIsSidebarOpen(false); setShowNotifications(false); }}
      />

      {/* ════ LEFT NAV SIDEBAR ════ */}
      <div className={`hdr-drawer nav-drawer ${isSidebarOpen ? "open" : ""}`}>
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e293b 0%, #334155 50%, #3b82f6 100%)",
            padding: "28px 20px 24px",
            display: "flex", flexDirection: "column", alignItems: "center",
            borderRadius: "0 0 24px 0",
          }}
        >
          <div style={{
            width: 80, height: 80, borderRadius: "50%",
            background: "#fff", padding: 3, marginBottom: 12,
            boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
          }}>
            {profile.cust_img ? (
              <img src={`${IMAGE_BASE_URL}${profile.cust_img}`} alt="avatar"
                style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              <div style={{
                width: "100%", height: "100%", borderRadius: "50%",
                background: "#e7f1ff", display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: 28, fontWeight: 700, color: "#0d6efd",
              }}>
                {(profile.fullName || "C").charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "#fff", letterSpacing: 0.5 }}>
            {profile.fullName}
          </p>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
            {profile.customerId || "—"}
          </p>
        </div>

        {/* Links */}
        <div style={{ flex: 1, overflowY: "auto", padding: "10px 0" }}>
          {sidebarItems.map(item => (
            <div
              key={item.path}
              className={`sidebar-link ${isActive(item.path) ? "active" : ""}`}
              onClick={() => { setIsSidebarOpen(false); navigate(item.path); }}
            >
              <item.icon size={19} style={{ marginRight: 14, flexShrink: 0 }} />
              {item.label}
            </div>
          ))}
        </div>

        {/* Logout */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid #f0f0f0" }}>
          <button
            onClick={handleLogout}
            style={{
              width: "100%", padding: "12px 0", border: "none",
              borderRadius: 10, background: "#fff5f5", color: "#dc3545",
              fontWeight: 600, fontSize: 14, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            <LogOut size={17} /> Logout
          </button>
        </div>
      </div>

      {/* ════ RIGHT NOTIFICATION DRAWER ════ */}
      <div className={`hdr-drawer notif-drawer ${showNotifications ? "open" : ""}`}>
        {/* Header */}
        <div style={{
          padding: "18px 18px 14px",
          borderBottom: "1px solid #f0f0f0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: "50%",
              background: "#eef4ff",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Bell size={17} color="#0d6efd" />
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>Notifications</p>
              {unreadCount > 0 && (
                <p style={{ margin: 0, fontSize: 11, color: "#6c757d" }}>{unreadCount} unread</p>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowNotifications(false)}
            style={{
              width: 32, height: 32, borderRadius: "50%", border: "none",
              background: "#f8f9fa", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={16} color="#6c757d" />
          </button>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 12px" }}>
          {notifications.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 24px", color: "#adb5bd" }}>
              <Bell size={36} style={{ marginBottom: 12, opacity: 0.35 }} />
              <p style={{ margin: 0, fontSize: 14 }}>No notifications yet</p>
            </div>
          ) : (
            notifications.map(note => (
              <div
                key={note.id}
                className={`notif-item ${!note.isRead ? "unread" : ""}`}
                onClick={() => {
                  if (note.bookingId) {
                    setShowNotifications(false);
                    navigate(`/active-trip/${note.bookingId}`);
                  }
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                  background: note.bookingId ? "#eef4ff" : "#f8f9fa",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {note.bookingId
                    ? <Clock size={16} color="#0d6efd" />
                    : <Bell  size={16} color="#6c757d" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: "0 0 3px", fontSize: 13, fontWeight: 600, color: "#1a1a2e", lineHeight: 1.4 }}>
                    {note.message}
                  </p>
                  <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>
                    {note.time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    {" · "}
                    {note.time.toLocaleDateString([], { day: "numeric", month: "short" })}
                  </p>
                </div>
                {!note.isRead && (
                  <div style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#0d6efd", flexShrink: 0, marginTop: 4,
                  }} />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ════ TOP HEADER ════ */}
      <header style={{
        height: "calc(62px + env(safe-area-inset-top, 32px))", /* Adds status bar height to total height */
        background: "#fff",
        borderBottom: "1px solid #f0f0f0",
        position: "sticky", top: 0, zIndex: 900,
        display: "flex", alignItems: "center",
        padding: "env(safe-area-inset-top, 32px) 14px 0 14px", /* Pushes internal content down safely */
        boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
      }}>
        {/* Left: burger + logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
          <button
            onClick={() => setIsSidebarOpen(true)}
            style={{
              width: 38, height: 38, borderRadius: 10, border: "none",
              background: "#f5f6fa", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Menu size={22} color="#333" />
          </button>

          <div onClick={() => navigate("/dashboard")} style={{ display: "flex", alignItems: "baseline", cursor: "pointer", marginLeft: 4 }}>
            <span style={{ fontWeight: 800, fontSize: 26, lineHeight: 1, color: "#e9830e" }}>P</span>
            <span style={{ fontWeight: 800, fontSize: 13, lineHeight: 1, color: "#e9830e", marginLeft: -1 }}>ATRA</span>
            <span style={{ fontWeight: 600, fontSize: 12, lineHeight: 1, color: "#333", marginLeft: 5 }}>TRAVELS PVT. LTD.</span>
          </div>
        </div>

        {/* Right: Bell, SOS, Logout */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Bell */}
          <button
            onClick={() => setShowNotifications(true)}
            style={{
              width: 38, height: 38, borderRadius: 10, border: "none",
              background: "#f5f6fa", cursor: "pointer", position: "relative",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Bell size={20} color="#555" />
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: 5, right: 5,
                width: 17, height: 17, borderRadius: "50%",
                background: "#dc3545", color: "#fff",
                fontSize: 10, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #fff",
              }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* SOS */}
          <button
            className="sos-pulse-always"
            onClick={() => { setShowSosModal(true); setSosCountdown(3); }}
            style={{
              width: 38, height: 38, borderRadius: 10, border: "none",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <Siren size={20} color="#fff" fill="#fff" />
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            style={{
              width: 38, height: 38, borderRadius: 10, border: "none",
              background: "#fff5f5", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <LogOut size={19} color="#dc3545" />
          </button>
        </div>
      </header>

      {/* ════ MAIN CONTENT ════ */}
      <main className="main-content-area">
        <Outlet />
      </main>

      {/* ════ BOTTOM NAV ════ */}
      <nav className="bottom-nav-container d-flex justify-content-around align-items-center d-md-none"
        style={{ height: 62 }}>
        {bottomNavItems.map(item => (
          <div
            key={item.path}
            onClick={() => navigate(item.path)}
            style={{
              flex: 1, height: "100%",
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              cursor: "pointer", gap: 3,
            }}
          >
            <item.icon
              size={22}
              color={isActive(item.path) ? "#0d6efd" : "#adb5bd"}
              strokeWidth={isActive(item.path) ? 2.5 : 1.8}
            />
            <span style={{
              fontSize: 10, fontWeight: isActive(item.path) ? 700 : 500,
              color: isActive(item.path) ? "#0d6efd" : "#adb5bd",
            }}>
              {item.label}
            </span>
            {isActive(item.path) && (
              <div style={{
                position: "absolute", bottom: 4,
                width: 4, height: 4, borderRadius: "50%", background: "#0d6efd",
              }} />
            )}
          </div>
        ))}
      </nav>

      {/* ════ SOS MODAL ════ */}
      {showSosModal && (
        <>
          <div
            style={{
              position: "fixed", inset: 0, zIndex: 10000,
              background: "rgba(0,0,0,0.84)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "24px",
            }}
          >
            <div
              className="sos-card-anim"
              style={{
                background: "#111113",
                border: "0.5px solid rgba(220,53,69,0.28)",
                borderRadius: 30,
                padding: "38px 28px 28px",
                maxWidth: 360, width: "100%",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Ambient top glow */}
              <div style={{
                position: "absolute", top: 0, left: "50%",
                transform: "translateX(-50%)",
                width: 240, height: 140,
                background: "radial-gradient(ellipse at 50% 0%, rgba(220,53,69,0.2) 0%, transparent 70%)",
                pointerEvents: "none",
              }} />

              {/* Badge */}
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "rgba(220,53,69,0.15)",
                border: "0.5px solid rgba(220,53,69,0.35)",
                borderRadius: 20, padding: "5px 14px",
                marginBottom: 10,
              }}>
                <Siren size={14} color="#dc3545" />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#dc3545", letterSpacing: 2, textTransform: "uppercase" }}>
                  Emergency SOS
                </span>
              </div>

              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", margin: "0 0 28px", fontWeight: 400 }}>
                Alerting admins with your live GPS location
              </p>

              {/* Radar countdown */}
              <div style={{
                position: "relative", width: 148, height: 148,
                margin: "0 auto 26px",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <div className="sos-ring" />
                <div className="sos-ring sos-ring-2" />
                <div className="sos-ring sos-ring-3" />
                <div style={{
                  width: 104, height: 104, borderRadius: "50%",
                  background: "rgba(220,53,69,0.1)",
                  border: "2px solid rgba(220,53,69,0.55)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  animation: "sosPulse 1.4s infinite",
                  position: "relative", zIndex: 2,
                }}>
                  <span
                    key={sosCountdown}
                    className="sos-count-anim"
                    style={{
                      fontSize: 58, fontWeight: 800,
                      color: "#dc3545", lineHeight: 1,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {sosCountdown}
                  </span>
                </div>
              </div>

              {/* GPS status */}
              <div style={{ marginBottom: 26 }}>
                <span className="sos-loc-pill">
                  <span className="sos-loc-dot" />
                  GPS location acquired
                </span>
              </div>

              {/* Buttons */}
              <button
                className="sos-send-btn"
                onClick={() => { setShowSosModal(false); handleTriggerSosApi(); }}
              >
                <Siren size={19} /> Send SOS Now
              </button>

              <button
                className="sos-cancel-btn"
                onClick={() => setShowSosModal(false)}
              >
                <X size={17} color="rgba(255,255,255,0.45)" /> Cancel
              </button>

              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.16)", margin: "18px 0 0", lineHeight: 1.6 }}>
                Auto-sends in {sosCountdown}s · Admins receive your exact coordinates instantly
              </p>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default HeaderLayout;